// src/pages/IroningAssignments.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField,
  Button, Paper, Stack, Chip, Alert, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { Save, Assignment } from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';
import { getData, saveData } from '../services/storageService';

const IroningAssignments = () => {
  const { t } = useI18n();
  const [assignments, setAssignments] = useState({
    introductor: {
      via1: { worker: '', pieces: '' },
      via2: { worker: '', pieces: '' },
      via3: { worker: '', pieces: '' }
    },
    hibrida: {
      via1: { worker: '', pieces: '' },
      via2: { worker: '', pieces: '' },
      via3: { worker: '', pieces: '' },
      via4: { worker: '', pieces: '' },
      via5: { worker: '', pieces: '' }
    },
    kannegiesser: [
      { machine: 1, worker1: '', worker2: '', worker3: '', pieces1: '', pieces2: '', pieces3: '', pieces4: '' },
      { machine: 2, worker1: '', worker2: '', worker3: '', pieces1: '', pieces2: '', pieces3: '', pieces4: '' },
      { machine: 3, worker1: '', worker2: '', worker3: '', pieces1: '', pieces2: '', pieces3: '', pieces4: '' },
      { machine: 4, worker1: '', worker2: '', worker3: '', pieces1: '', pieces2: '', pieces3: '', pieces4: '' }
    ]
  });
  const [staff, setStaff] = useState([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Завантажуємо персонал
      const staffData = await getData('staff') || [];
      const activeStaff = staffData.filter(s => s.status === 'active').map(s => s.name);
      setStaff(activeStaff);

      // Завантажуємо збережені призначення
      const savedAssignments = await getData('ironing_assignments');
      if (savedAssignments) {
        setAssignments(savedAssignments);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleInputChange = (section, via, field, value) => {
    setAssignments(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [via]: {
          ...prev[section][via],
          [field]: value
        }
      }
    }));
  };

  const handleKannegiesserChange = (machineIndex, field, value) => {
    setAssignments(prev => ({
      ...prev,
      kannegiesser: prev.kannegiesser.map((machine, index) =>
        index === machineIndex ? { ...machine, [field]: value } : machine
      )
    }));
  };

  const handleSave = async () => {
    try {
      await saveData('ironing_assignments', assignments);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving assignments:', error);
    }
  };

  const ViaCard = ({ title, via, section, data }) => (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {title} {via}
      </Typography>
      <Stack spacing={2}>
        <TextField
          select
          label={t('ironing_worker')}
          value={data.worker}
          onChange={(e) => handleInputChange(section, `via${via}`, 'worker', e.target.value)}
          fullWidth
          size="small"
          SelectProps={{
            native: true,
          }}
        >
          <option value=""></option>
          {staff.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </TextField>
        <TextField
          label={t('ironing_pieces_count')}
          type="number"
          value={data.pieces}
          onChange={(e) => handleInputChange(section, `via${via}`, 'pieces', e.target.value)}
          fullWidth
          size="small"
        />
      </Stack>
    </Paper>
  );

  const KannegiesserMachineCard = ({ machine, index }) => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('ironing_kannegiesser_title')} {machine.machine}
      </Typography>
      <Grid container spacing={2}>
        {/* Працівники */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>{t('section_trabajadores')}</Typography>
          <Stack spacing={2}>
            <TextField
              select
              label={`${t('ironing_worker')} 1`}
              value={machine.worker1}
              onChange={(e) => handleKannegiesserChange(index, 'worker1', e.target.value)}
              fullWidth
              size="small"
              SelectProps={{ native: true }}
            >
              <option value=""></option>
              {staff.map(name => <option key={name} value={name}>{name}</option>)}
            </TextField>
            <TextField
              select
              label={`${t('ironing_worker')} 2`}
              value={machine.worker2}
              onChange={(e) => handleKannegiesserChange(index, 'worker2', e.target.value)}
              fullWidth
              size="small"
              SelectProps={{ native: true }}
            >
              <option value=""></option>
              {staff.map(name => <option key={name} value={name}>{name}</option>)}
            </TextField>
            <TextField
              select
              label={`${t('ironing_worker')} 3`}
              value={machine.worker3}
              onChange={(e) => handleKannegiesserChange(index, 'worker3', e.target.value)}
              fullWidth
              size="small"
              SelectProps={{ native: true }}
            >
              <option value=""></option>
              {staff.map(name => <option key={name} value={name}>{name}</option>)}
            </TextField>
          </Stack>
        </Grid>

        {/* Кількість деталей */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>{t('labels_kpi')}</Typography>
          <Stack spacing={2}>
            <TextField
              label={t('field_pieza1')}
              type="number"
              value={machine.pieces1}
              onChange={(e) => handleKannegiesserChange(index, 'pieces1', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t('field_pieza2')}
              type="number"
              value={machine.pieces2}
              onChange={(e) => handleKannegiesserChange(index, 'pieces2', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t('field_pieza3')}
              type="number"
              value={machine.pieces3}
              onChange={(e) => handleKannegiesserChange(index, 'pieces3', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t('field_pieza4')}
              type="number"
              value={machine.pieces4}
              onChange={(e) => handleKannegiesserChange(index, 'pieces4', e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Assignment sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {t('ironing_assignments_title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('ironing_assignments_subtitle')}
              </Typography>
            </Box>
          </Stack>

          {saved && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('ironing_assignments_saved')}
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            size="large"
          >
            {t('ironing_save_assignments')}
          </Button>
        </CardContent>
      </Card>

      {/* Introductor Piezas Pequeñas - 3 робочі місця */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary">
            {t('ironing_introductor_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('tech_form_tab_introductor')} - 3 {t('ironing_via').toLowerCase()}s
          </Typography>
          
          <Grid container spacing={3}>
            {[1, 2, 3].map(via => (
              <Grid item xs={12} md={4} key={via}>
                <ViaCard
                  title={`${t('ironing_via')}`}
                  via={via}
                  section="introductor"
                  data={assignments.introductor[`via${via}`]}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Máquina Híbrida - 5 робочих місць */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary">
            {t('ironing_hibrida_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('tech_form_tab_hibrida')} - 5 {t('ironing_via').toLowerCase()}s
          </Typography>
          
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5].map(via => (
              <Grid item xs={12} md={2.4} key={via}>
                <ViaCard
                  title={`${t('ironing_via')}`}
                  via={via}
                  section="hibrida"
                  data={assignments.hibrida[`via${via}`]}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Máquinas Kannegiesser - 4 машини */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom color="primary">
            {t('ironing_kannegiesser_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('tech_form_tab_kannegiesser')} - 4 {t('machine').toLowerCase()}s
          </Typography>
          
          {assignments.kannegiesser.map((machine, index) => (
            <KannegiesserMachineCard
              key={machine.machine}
              machine={machine}
              index={index}
            />
          ))}
        </CardContent>
      </Card>

      {/* Summary Table */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('view_shift_summary')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('machine')}</strong></TableCell>
                  <TableCell><strong>{t('ironing_worker')}</strong></TableCell>
                  <TableCell><strong>{t('ironing_pieces_count')}</strong></TableCell>
                  <TableCell><strong>{t('common_status')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Introductor rows */}
                {Object.entries(assignments.introductor).map(([via, data]) => (
                  <TableRow key={`introductor-${via}`}>
                    <TableCell>{t('ironing_introductor_title')} {via}</TableCell>
                    <TableCell>{data.worker || '-'}</TableCell>
                    <TableCell>{data.pieces || '0'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={data.worker ? t('status_active') : t('status_pending')} 
                        color={data.worker ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Hibrida rows */}
                {Object.entries(assignments.hibrida).map(([via, data]) => (
                  <TableRow key={`hibrida-${via}`}>
                    <TableCell>{t('ironing_hibrida_title')} {via}</TableCell>
                    <TableCell>{data.worker || '-'}</TableCell>
                    <TableCell>{data.pieces || '0'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={data.worker ? t('status_active') : t('status_pending')} 
                        color={data.worker ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default IroningAssignments;